import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from '../../FormElements/PageTitle';

function AlertsInboxPage() {
	const [
		selectedCategory,
		setSelectedCategory
	] = useState('all');
	const [
		selectedAlert,
		setSelectedAlert
	] = useState(null);

	// Mock alert data - will be replaced with real backend data later
	const mockAlerts = [
		{
			id: 1,
			category: 'trade',
			priority: 'high',
			title: 'Trade Alert: Dynasty League',
			timestamp: '2 hours ago',
			read: false,
			roster: 'The Gronk Squad',
			message: 'You received a trade offer from Team Chaos.',
			details: {
				offering: [
					'Justin Jefferson WR',
					'2024 1st Round Pick'
				],
				receiving: [
					"Ja'Marr Chase WR",
					'Tony Pollard RB'
				],
				expiresIn: '24 hours'
			}
		},
		{
			id: 2,
			category: 'injury',
			priority: 'critical',
			title: 'Injury Report: Key Player Down',
			timestamp: '3 hours ago',
			read: false,
			roster: 'The Gronk Squad',
			message: 'Christian McCaffrey listed as QUESTIONABLE for Week 12.',
			details: {
				player: 'Christian McCaffrey',
				team: 'SF',
				injury: 'Knee',
				status: 'Questionable',
				impact: 'Limited practice participation. Monitor throughout the week.'
			}
		},
		{
			id: 3,
			category: 'news',
			priority: 'medium',
			title: 'Breaking News: Waiver Wire Gem',
			timestamp: '5 hours ago',
			read: true,
			roster: 'Championship Chasers',
			message: 'Rashee Rice seeing increased target share.',
			details: {
				player: 'Rashee Rice',
				team: 'KC',
				insight:
					"With injuries to the WR corps, Rice has emerged as Mahomes' primary target. 12 targets in the last game.",
				recommendation: 'High priority waiver add if available.'
			}
		},
		{
			id: 4,
			category: 'roster',
			priority: 'medium',
			title: 'Roster Alert: Lineup Optimization',
			timestamp: '1 day ago',
			read: true,
			roster: 'The Gronk Squad',
			message: 'You have better options on your bench this week.',
			details: {
				suggestion: 'Consider starting Jaylen Warren over your current RB2.',
				reasoning:
					'Warren faces a bottom-5 run defense and has scored in 3 straight games.',
				projectedPoints: '+4.2 points'
			}
		},
		{
			id: 5,
			category: 'trade',
			priority: 'high',
			title: 'Trade Accepted: Dynasty League',
			timestamp: '2 days ago',
			read: true,
			roster: 'Championship Chasers',
			message: 'Your trade has been accepted and processed.',
			details: {
				acquired: [
					'CeeDee Lamb WR',
					'2024 2nd Round Pick'
				],
				traded: [
					'Stefon Diggs WR',
					'Dameon Pierce RB'
				],
				status: 'Complete'
			}
		},
		{
			id: 6,
			category: 'matchup',
			priority: 'low',
			title: 'Weekly Matchup Preview',
			timestamp: '3 days ago',
			read: true,
			roster: 'The Gronk Squad',
			message: "This week's matchup analysis is ready.",
			details: {
				opponent: 'Team Elite',
				yourProjection: '142.8 points',
				theirProjection: '138.2 points',
				winProbability: '58%',
				keyMatchup: 'Your Travis Kelce vs Their Mark Andrews'
			}
		},
		{
			id: 7,
			category: 'injury',
			priority: 'critical',
			title: 'Injury Update: Starter OUT',
			timestamp: '4 days ago',
			read: true,
			roster: 'Championship Chasers',
			message: 'Kenneth Walker III ruled OUT for Sunday.',
			details: {
				player: 'Kenneth Walker III',
				team: 'SEA',
				injury: 'Ankle',
				status: 'Out',
				impact:
					'Zach Charbonnet expected to handle lead-back duties. Add immediately if available.'
			}
		},
		{
			id: 8,
			category: 'news',
			priority: 'medium',
			title: 'Coaching Change Impact',
			timestamp: '5 days ago',
			read: true,
			roster: 'The Gronk Squad',
			message: 'New offensive coordinator could boost your players.',
			details: {
				team: 'Carolina Panthers',
				change: 'New OC hired - known for pass-heavy schemes',
				affectedPlayers: 'Bryce Young, Adam Thielen',
				outlook: 'Positive trend for pass-catchers'
			}
		}
	];

	const categories = [
		{ id: 'all', name: 'All Alerts', icon: 'fa-inbox' },
		{ id: 'trade', name: 'Trades', icon: 'fa-exchange-alt' },
		{ id: 'injury', name: 'Injuries', icon: 'fa-user-injured' },
		{ id: 'news', name: 'News', icon: 'fa-newspaper' },
		{ id: 'roster', name: 'Roster', icon: 'fa-users' },
		{ id: 'matchup', name: 'Matchups', icon: 'fa-trophy' }
	];

	const getPriorityIndicator = (priority) => {
		switch (priority) {
			case 'critical':
				return 'border-l-4 border-red-500';
			case 'high':
				return 'border-l-4 border-orange-500';
			case 'medium':
				return 'border-l-4 border-blue-500';
			case 'low':
				return 'border-l-4 border-gray-400';
			default:
				return 'border-l-4 border-gray-400';
		}
	};

	const getPriorityBadge = (priority) => {
		switch (priority) {
			case 'critical':
				return (
					<span className="px-2 py-1 text-xs font-bold text-white bg-red-600 rounded-full">
						URGENT
					</span>
				);
			case 'high':
				return (
					<span className="px-2 py-1 text-xs font-bold text-white bg-orange-500 rounded-full">
						HIGH
					</span>
				);
			case 'medium':
				return (
					<span className="px-2 py-1 text-xs font-bold text-white bg-blue-500 rounded-full">
						NORMAL
					</span>
				);
			case 'low':
				return (
					<span className="px-2 py-1 text-xs font-bold text-gray-600 bg-gray-300 rounded-full">
						LOW
					</span>
				);
			default:
				return null;
		}
	};

	const getCategoryIcon = (category) => {
		const cat = categories.find((c) => c.id === category);
		return cat ? cat.icon : 'fa-inbox';
	};

	const filteredAlerts =
		selectedCategory === 'all'
			? mockAlerts
			: mockAlerts.filter((alert) => alert.category === selectedCategory);

	const unreadCount = mockAlerts.filter((alert) => !alert.read).length;

	const handleAlertClick = (alert) => {
		setSelectedAlert(alert);
	};

	const handleCloseDetail = () => {
		setSelectedAlert(null);
	};

	const handleBackToDashboard = () => {
		window.location.href = '/front-office';
	};

	const renderAlertDetails = () => {
		if (!selectedAlert) return null;

		return (
			<div
				className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
				onClick={handleCloseDetail}
			>
				<div
					className="bg-white dark:bg-dark-card border border-gray-300 dark:border-dark-border rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto"
					onClick={(e) => e.stopPropagation()}
				>
					{/* Header */}
					<div className="flex items-start justify-between p-6 pb-4 border-b border-gray-200 dark:border-dark-border">
						<div className="flex-1">
							<div className="flex items-center gap-3 mb-3">
								<i
									className={`fa-solid ${getCategoryIcon(
										selectedAlert.category
									)} text-3xl text-blue-500`}
								/>
								<div>
									<h3 className="text-xl font-bold text-gray-900 dark:text-dark-text">
										{selectedAlert.title}
									</h3>
									<p className="text-sm text-gray-500 dark:text-gray-400">
										{selectedAlert.timestamp}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-2">
								{getPriorityBadge(selectedAlert.priority)}
								<span className="px-3 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
									{selectedAlert.roster}
								</span>
							</div>
						</div>
						<button
							onClick={handleCloseDetail}
							className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2"
						>
							<i className="fa-solid fa-times text-xl" />
						</button>
					</div>

					{/* Message */}
					<div className="p-6 pb-4">
						<p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed">
							{selectedAlert.message}
						</p>
					</div>

					{/* Details Section */}
					<div className="px-6 pb-6">
						<div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
							<h4 className="text-base font-bold text-gray-900 dark:text-dark-text mb-3 flex items-center">
								<i className="fa-solid fa-list-ul mr-2" />
								Details
							</h4>
							<div className="space-y-3">
								{selectedAlert.category === 'trade' && (
									<div className="space-y-3">
										{selectedAlert.details.offering && (
											<div>
												<p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
													They Offer:
												</p>
												<ul className="list-disc list-inside text-green-600 dark:text-green-400 text-sm">
													{selectedAlert.details.offering.map((item, idx) => (
														<li key={idx}>{item}</li>
													))}
												</ul>
											</div>
										)}
										{selectedAlert.details.receiving && (
											<div>
												<p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
													You Receive:
												</p>
												<ul className="list-disc list-inside text-blue-600 dark:text-blue-400 text-sm">
													{selectedAlert.details.receiving.map((item, idx) => (
														<li key={idx}>{item}</li>
													))}
												</ul>
											</div>
										)}
										{selectedAlert.details.expiresIn && (
											<p className="text-sm text-red-600 dark:text-red-400">
												<i className="fa-solid fa-clock mr-1" />
												Expires in: {selectedAlert.details.expiresIn}
											</p>
										)}
										{selectedAlert.details.acquired && (
											<div>
												<p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
													Acquired:
												</p>
												<ul className="list-disc list-inside text-green-600 dark:text-green-400 text-sm">
													{selectedAlert.details.acquired.map((item, idx) => (
														<li key={idx}>{item}</li>
													))}
												</ul>
											</div>
										)}
										{selectedAlert.details.traded && (
											<div>
												<p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
													Traded Away:
												</p>
												<ul className="list-disc list-inside text-red-600 dark:text-red-400 text-sm">
													{selectedAlert.details.traded.map((item, idx) => (
														<li key={idx}>{item}</li>
													))}
												</ul>
											</div>
										)}
									</div>
								)}
								{selectedAlert.category === 'injury' && (
									<div className="space-y-3">
										<div className="grid grid-cols-2 gap-3">
											<div>
												<p className="text-xs text-gray-500">Player</p>
												<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
													{selectedAlert.details.player}
												</p>
											</div>
											<div>
												<p className="text-xs text-gray-500">Team</p>
												<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
													{selectedAlert.details.team}
												</p>
											</div>
											<div>
												<p className="text-xs text-gray-500">Injury</p>
												<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
													{selectedAlert.details.injury}
												</p>
											</div>
											<div>
												<p className="text-xs text-gray-500">Status</p>
												<p
													className={`text-sm font-semibold ${selectedAlert
														.details.status === 'Out'
														? 'text-red-600 dark:text-red-400'
														: 'text-yellow-600 dark:text-yellow-400'}`}
												>
													{selectedAlert.details.status}
												</p>
											</div>
										</div>
										<div className="pt-3 border-t border-gray-200 dark:border-gray-700">
											<p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
												Impact Analysis
											</p>
											<p className="text-sm text-gray-700 dark:text-gray-300">
												{selectedAlert.details.impact}
											</p>
										</div>
									</div>
								)}
								{selectedAlert.category === 'news' && (
									<div className="space-y-3">
										{selectedAlert.details.player && (
											<div>
												<p className="text-xs text-gray-500">Player</p>
												<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
													{selectedAlert.details.player} -{' '}
													{selectedAlert.details.team}
												</p>
											</div>
										)}
										{selectedAlert.details.team &&
										!selectedAlert.details.player && (
											<div>
												<p className="text-xs text-gray-500">Team</p>
												<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
													{selectedAlert.details.team}
												</p>
											</div>
										)}
										{selectedAlert.details.insight && (
											<div>
												<p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
													Insight
												</p>
												<p className="text-sm text-gray-700 dark:text-gray-300">
													{selectedAlert.details.insight}
												</p>
											</div>
										)}
										{selectedAlert.details.recommendation && (
											<div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-20 p-3 rounded border border-blue-200 dark:border-blue-800">
												<p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1">
													<i className="fa-solid fa-lightbulb mr-1" />
													Recommendation
												</p>
												<p className="text-sm text-blue-900 dark:text-blue-300">
													{selectedAlert.details.recommendation}
												</p>
											</div>
										)}
										{selectedAlert.details.change && (
											<div>
												<p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
													Change
												</p>
												<p className="text-sm text-gray-700 dark:text-gray-300">
													{selectedAlert.details.change}
												</p>
											</div>
										)}
										{selectedAlert.details.affectedPlayers && (
											<div>
												<p className="text-xs text-gray-500">
													Affected Players
												</p>
												<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
													{selectedAlert.details.affectedPlayers}
												</p>
											</div>
										)}
										{selectedAlert.details.outlook && (
											<div>
												<p className="text-xs text-gray-500">Outlook</p>
												<p className="text-sm font-semibold text-green-600 dark:text-green-400">
													{selectedAlert.details.outlook}
												</p>
											</div>
										)}
									</div>
								)}
								{selectedAlert.category === 'roster' && (
									<div className="space-y-3">
										<div className="bg-green-50 dark:bg-green-900 dark:bg-opacity-20 p-3 rounded border border-green-200 dark:border-green-800">
											<p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-1">
												<i className="fa-solid fa-lightbulb mr-1" />
												Suggestion
											</p>
											<p className="text-sm text-green-900 dark:text-green-300">
												{selectedAlert.details.suggestion}
											</p>
										</div>
										<div>
											<p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
												Reasoning
											</p>
											<p className="text-sm text-gray-700 dark:text-gray-300">
												{selectedAlert.details.reasoning}
											</p>
										</div>
										<div className="flex items-center gap-2">
											<i className="fa-solid fa-chart-line text-green-600 dark:text-green-400 text-xl" />
											<span className="text-green-600 dark:text-green-400 font-bold text-base">
												{selectedAlert.details.projectedPoints}
											</span>
										</div>
									</div>
								)}
								{selectedAlert.category === 'matchup' && (
									<div className="space-y-3">
										<div className="grid grid-cols-2 gap-3">
											<div>
												<p className="text-xs text-gray-500">Opponent</p>
												<p className="text-sm font-semibold text-gray-900 dark:text-dark-text">
													{selectedAlert.details.opponent}
												</p>
											</div>
											<div>
												<p className="text-xs text-gray-500">Win Probability</p>
												<p className="text-base font-bold text-green-600 dark:text-green-400">
													{selectedAlert.details.winProbability}
												</p>
											</div>
										</div>
										<div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 space-y-2">
											<div className="flex justify-between items-center">
												<span className="text-xs text-gray-600 dark:text-gray-400">
													Your Projection
												</span>
												<span className="text-sm font-bold text-blue-600 dark:text-blue-400">
													{selectedAlert.details.yourProjection}
												</span>
											</div>
											<div className="flex justify-between items-center">
												<span className="text-xs text-gray-600 dark:text-gray-400">
													Their Projection
												</span>
												<span className="text-sm font-bold text-red-600 dark:text-red-400">
													{selectedAlert.details.theirProjection}
												</span>
											</div>
										</div>
										<div className="bg-purple-50 dark:bg-purple-900 dark:bg-opacity-20 p-3 rounded border border-purple-200 dark:border-purple-800">
											<p className="text-xs font-semibold text-purple-700 dark:text-purple-400 mb-1">
												<i className="fa-solid fa-trophy mr-1" />
												Key Matchup
											</p>
											<p className="text-sm text-purple-900 dark:text-purple-300">
												{selectedAlert.details.keyMatchup}
											</p>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="flex gap-3 p-6 pt-4 border-t border-gray-200 dark:border-dark-border">
						{selectedAlert.category === 'trade' &&
						selectedAlert.details.expiresIn && (
							<button className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm">
								<i className="fa-solid fa-eye mr-2" />
								View Trade
							</button>
						)}
						<button
							onClick={handleCloseDetail}
							className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm"
						>
							Close
						</button>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="container p-4 pt-4 lg:py-14 md:px-20 text-gray-800 dark:text-dark-text">
			<div className="mt-4">
				<div className="flex items-center justify-between mb-4">
					<PageTitle title="Alerts Inbox" />
					<Link
						to="/front-office"
						className="text-sm text-blue-500 hover:underline flex items-center gap-2"
					>
						<i className="fa-solid fa-arrow-left" />
						Back to Front Office
					</Link>
				</div>
				{/* Category Filters Card */}
				<div className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border shadow-lg rounded-xl mb-6">
					<div className="mx-4 py-3">
						<div className="flex items-center justify-between mb-3">
							<h3 className="text-sm font-semibold text-gray-700 dark:text-dark-text">
								Filter by Category
							</h3>
							{unreadCount > 0 && (
								<span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
									{unreadCount} Unread
								</span>
							)}
						</div>
						<div className="flex flex-wrap gap-2">
							{categories.map((category) => (
								<button
									key={category.id}
									onClick={() => setSelectedCategory(category.id)}
									className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selectedCategory ===
									category.id
										? 'bg-blue-500 text-white border-blue-500'
										: 'bg-white dark:bg-dark-card border-gray-400 dark:border-dark-border text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-700'}`}
								>
									<i className={`fa-solid ${category.icon} mr-1.5`} />
									{category.name}
								</button>
							))}
						</div>
					</div>
				</div>

				{/* Alerts List Card */}
				<div className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border shadow-lg rounded-xl">
					<div className="mx-4 py-3 border-b border-gray-200 dark:border-dark-border">
						<h2 className="text-lg font-bold text-gray-800 dark:text-dark-text">
							<i className="fa-solid fa-bell text-lg mr-3" />
							{selectedCategory === 'all' ? (
								'All Alerts'
							) : (
								(categories.find((c) => c.id === selectedCategory) || {}).name
							)}
							<span className="text-sm font-normal text-gray-500 ml-2">
								({filteredAlerts.length})
							</span>
						</h2>
					</div>
					{filteredAlerts.length === 0 ? (
						<div className="text-center py-12 px-4">
							<i className="fa-solid fa-inbox text-6xl text-gray-300 dark:text-gray-600 mb-4" />
							<p className="text-gray-500 dark:text-gray-400">
								No alerts in this category. You're all caught up!
							</p>
						</div>
					) : (
						<ul className="divide-y divide-gray-200 dark:divide-dark-border">
							{filteredAlerts.map((alert) => (
								<li
									key={alert.id}
									onClick={() => handleAlertClick(alert)}
									className={`px-4 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${getPriorityIndicator(
										alert.priority
									)} ${!alert.read
										? 'bg-blue-50 dark:bg-blue-900 dark:bg-opacity-10'
										: ''}`}
								>
									<div className="flex items-start gap-3">
										<div className="flex-shrink-0 mt-1">
											<i
												className={`fa-solid ${getCategoryIcon(
													alert.category
												)} text-xl ${alert.read
													? 'text-gray-400 dark:text-gray-500'
													: 'text-blue-500'}`}
											/>
										</div>
										<div className="flex-1 min-w-0">
											<div className="flex items-start justify-between gap-2 mb-1">
												<h3
													className={`text-sm font-semibold ${alert.read
														? 'text-gray-600 dark:text-gray-400'
														: 'text-gray-900 dark:text-dark-text'}`}
												>
													{alert.title}
												</h3>
												<span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
													{alert.timestamp}
												</span>
											</div>
											<p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
												{alert.message}
											</p>
											<div className="flex flex-wrap items-center gap-2">
												{getPriorityBadge(alert.priority)}
												<span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
													{alert.roster}
												</span>
												{!alert.read && (
													<span className="px-2 py-0.5 text-xs font-semibold text-blue-600 dark:text-blue-400">
														• NEW
													</span>
												)}
											</div>
										</div>
										<div className="flex-shrink-0">
											<i className="fa-solid fa-chevron-right text-gray-400 dark:text-gray-500 text-sm" />
										</div>
									</div>
								</li>
							))}
						</ul>
					)}
				</div>

				{/* Stats Card */}
				<div className="mt-6 bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border shadow-lg rounded-xl">
					<div className="mx-4 py-3 border-b border-gray-200 dark:border-dark-border">
						<h3 className="text-sm font-semibold text-gray-700 dark:text-dark-text">
							Alert Stats
						</h3>
					</div>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
						<div className="text-center">
							<p className="text-2xl font-bold text-gray-800 dark:text-dark-text">
								{mockAlerts.length}
							</p>
							<p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
						</div>
						<div className="text-center">
							<p className="text-2xl font-bold text-red-500">{unreadCount}</p>
							<p className="text-xs text-gray-500 dark:text-gray-400">Unread</p>
						</div>
						<div className="text-center">
							<p className="text-2xl font-bold text-orange-500">
								{
									mockAlerts.filter(
										(a) => a.priority === 'high' || a.priority === 'critical'
									).length
								}
							</p>
							<p className="text-xs text-gray-500 dark:text-gray-400">High Priority</p>
						</div>
						<div className="text-center">
							<p className="text-2xl font-bold text-blue-500">2</p>
							<p className="text-xs text-gray-500 dark:text-gray-400">Rosters</p>
						</div>
					</div>
				</div>
			</div>

			{/* Alert Detail Modal */}
			{renderAlertDetails()}
		</div>
	);
}

export default AlertsInboxPage;
